import { defaultShouldDehydrateQuery, onlineManager, QueryClient } from '@tanstack/react-query';
import {
  type PersistedClient,
  type PersistQueryClientProviderProps,
} from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import {
  getPreviousSessionOwner,
  getSessionOwner,
  isAuthRequiredError,
  ObsoleteSessionError,
  shouldRetryRequest,
} from '@/features/auth/session-boundary';

import { AttachmentReadError } from '@/features/shiftbook/api/attachment-upload';

const OFFLINE_FIRST = 'offlineFirst';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryRequest,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      networkMode: OFFLINE_FIRST,
      staleTime: 60_000,
    },
    mutations: {
      retry: (count, error) =>
        !isAuthRequiredError(error) &&
        !(error instanceof ObsoleteSessionError) &&
        !(error instanceof AttachmentReadError) &&
        count < 1,
      networkMode: OFFLINE_FIRST,
    },
  },
});

const PERSIST_KEY = 'blms-query-cache';

type PersistedMutationState = {
  status?: string;
  isPaused?: boolean;
  variables?: unknown;
};

type PersistedMutation = {
  mutationKey?: unknown;
  state?: PersistedMutationState;
};

type PersistedQuery = {
  queryKey?: unknown;
};

const AUTH_SESSION_QUERY_KEY = 'auth-session';

type PersistedClientSanitizeOptions = {
  dropAuthSessionQueries?: boolean;
  dropShiftbookQueries?: boolean;
  dropOrphanedPendingMutations?: boolean;
  dropAllMutations?: boolean;
  dropUncloneableQueries?: boolean;
  dropUncloneableMutations?: boolean;
  dropLegacyKeywordMutations?: boolean;
  convertLegacyResponsibleMutations?: boolean;
};

function isOrphanedPendingMutation(mutation: PersistedMutation): boolean {
  return mutation.state?.status === 'pending' && mutation.state?.isPaused !== true;
}

function isPersistedWorkspaceSaveMutation(mutation: PersistedMutation): boolean {
  return (
    Array.isArray(mutation.mutationKey) &&
    mutation.mutationKey[0] === 'shiftbook-entry-workspace-save'
  );
}

function hasLegacyKeywordPayload(variables: unknown): boolean {
  if (!variables || typeof variables !== 'object') {
    return false;
  }

  const request = (variables as { request?: unknown }).request;
  if (!request || typeof request !== 'object') {
    return false;
  }

  const entryPayload = (request as { entry?: unknown }).entry;
  if (!entryPayload || typeof entryPayload !== 'object') {
    return false;
  }

  if ((entryPayload as { mode?: unknown }).mode === 'create') {
    return Array.isArray((entryPayload as { entry?: { keywords?: unknown } }).entry?.keywords);
  }

  if ((entryPayload as { mode?: unknown }).mode === 'update') {
    return Array.isArray((entryPayload as { patch?: { keywords?: unknown } }).patch?.keywords);
  }

  return false;
}

// Pre-ENG-167 workspace saves carried responsibleUserId; replaying them
// against the strict current API is a guaranteed 400 that would silently
// consume the paused mutation. AG-10: convert the queued payload in place so
// the operator's offline save still replays; only unconvertible shapes are
// dropped (same policy as legacy keyword payloads).
function hasLegacyResponsiblePayload(variables: unknown): boolean {
  if (!variables || typeof variables !== 'object') {
    return false;
  }

  const request = (variables as { request?: unknown }).request;
  if (!request || typeof request !== 'object') {
    return false;
  }

  const entryPayload = (request as { entry?: unknown }).entry;
  if (!entryPayload || typeof entryPayload !== 'object') {
    return false;
  }

  if ((entryPayload as { mode?: unknown }).mode === 'create') {
    const entry = (entryPayload as { entry?: unknown }).entry;
    return Boolean(entry && typeof entry === 'object' && 'responsibleUserId' in entry);
  }

  if ((entryPayload as { mode?: unknown }).mode === 'update') {
    const patch = (entryPayload as { patch?: unknown }).patch;
    return Boolean(patch && typeof patch === 'object' && 'responsibleUserId' in patch);
  }

  return false;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function convertLegacyResponsibleContainer(
  container: Record<string, unknown>,
): { ok: true; value: Record<string, unknown> } | { ok: false } {
  const { responsibleUserId, ...rest } = container;
  if ('responsible' in rest) {
    return { ok: true, value: rest };
  }

  if (responsibleUserId === '' || responsibleUserId === null || responsibleUserId === undefined) {
    return { ok: true, value: { ...rest, responsible: null } };
  }

  if (typeof responsibleUserId === 'string' && UUID_PATTERN.test(responsibleUserId)) {
    return { ok: true, value: { ...rest, responsible: { kind: 'user', id: responsibleUserId } } };
  }

  return { ok: false };
}

// Only called after hasLegacyResponsiblePayload matched, so the
// request.entry shape is known-present. Returns null when the legacy value
// cannot be converted; the caller drops the mutation in that case.
function convertLegacyResponsibleMutation(mutation: PersistedMutation): PersistedMutation | null {
  const variables = mutation.state?.variables as {
    request: { entry: Record<string, unknown> };
  } & Record<string, unknown>;
  const entryPayload = variables.request.entry;
  const containerKey = entryPayload.mode === 'create' ? 'entry' : 'patch';
  const container = entryPayload[containerKey];
  if (!container || typeof container !== 'object') {
    return null;
  }

  const converted = convertLegacyResponsibleContainer(container as Record<string, unknown>);
  if (!converted.ok) {
    return null;
  }

  return {
    ...mutation,
    state: {
      ...mutation.state,
      variables: {
        ...variables,
        request: {
          ...variables.request,
          entry: { ...entryPayload, [containerKey]: converted.value },
        },
      },
    },
  };
}

function isPersistedAuthSessionQuery(query: PersistedQuery): boolean {
  return Array.isArray(query.queryKey) && query.queryKey[0] === AUTH_SESSION_QUERY_KEY;
}

function isPersistedShiftbookQuery(query: PersistedQuery): boolean {
  return (
    Array.isArray(query.queryKey) &&
    typeof query.queryKey[0] === 'string' &&
    query.queryKey[0].startsWith('shiftbook-')
  );
}

function isDataCloneError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'DataCloneError' || /cannot be cloned|can not be cloned/i.test(error.message))
  );
}

function isStructuredCloneable(value: unknown): boolean {
  if (typeof structuredClone !== 'function') {
    return true;
  }

  try {
    structuredClone(value);
    return true;
  } catch {
    return false;
  }
}

export function removeOrphanedPendingMutations(client: QueryClient) {
  const mutationCache = client.getMutationCache();
  const mutations = mutationCache.getAll();

  for (const mutation of mutations) {
    const candidate = mutation as PersistedMutation;
    if (isOrphanedPendingMutation(candidate)) {
      mutationCache.remove(mutation);
    }
  }
}

function sanitizePersistedClient(
  client: PersistedClient,
  options: PersistedClientSanitizeOptions,
): {
  client: PersistedClient;
  didSanitize: boolean;
} {
  const queries = client.clientState?.queries;
  const mutations = client.clientState?.mutations;
  const filteredQueries = Array.isArray(queries)
    ? queries.filter((query) => {
        const persistedQuery = query as PersistedQuery;
        if (options.dropAuthSessionQueries && isPersistedAuthSessionQuery(persistedQuery)) {
          return false;
        }

        if (options.dropShiftbookQueries && isPersistedShiftbookQuery(persistedQuery)) {
          return false;
        }

        return true;
      })
    : queries;
  let didConvertMutations = false;
  const filteredMutations = Array.isArray(mutations)
    ? mutations.flatMap((mutation) => {
        let persistedMutation = mutation as PersistedMutation;
        if (options.dropAllMutations) {
          return [];
        }

        if (
          options.dropLegacyKeywordMutations &&
          isPersistedWorkspaceSaveMutation(persistedMutation) &&
          hasLegacyKeywordPayload(persistedMutation.state?.variables)
        ) {
          return [];
        }

        if (
          options.convertLegacyResponsibleMutations &&
          isPersistedWorkspaceSaveMutation(persistedMutation) &&
          hasLegacyResponsiblePayload(persistedMutation.state?.variables)
        ) {
          const converted = convertLegacyResponsibleMutation(persistedMutation);
          if (!converted) {
            return [];
          }
          didConvertMutations = true;
          persistedMutation = converted;
        }

        if (options.dropOrphanedPendingMutations && isOrphanedPendingMutation(persistedMutation)) {
          return [];
        }

        if (options.dropUncloneableMutations && !isStructuredCloneable(persistedMutation)) {
          return [];
        }

        return [persistedMutation as typeof mutation];
      })
    : mutations;
  const cloneableQueries = Array.isArray(filteredQueries)
    ? filteredQueries.filter((query) => {
        if (options.dropUncloneableQueries && !isStructuredCloneable(query)) {
          return false;
        }

        return true;
      })
    : filteredQueries;

  const didFilterQueries = Array.isArray(queries) && cloneableQueries.length !== queries.length;
  const didFilterMutations =
    didConvertMutations ||
    (Array.isArray(mutations) && filteredMutations.length !== mutations.length);

  if (!didFilterQueries && !didFilterMutations) {
    return { client, didSanitize: false };
  }

  return {
    client: {
      ...client,
      clientState: {
        ...client.clientState,
        queries: cloneableQueries,
        mutations: filteredMutations,
      },
    },
    didSanitize: true,
  };
}

async function writePersistedClient(client: PersistedClient): Promise<void> {
  try {
    await set(PERSIST_KEY, client);
  } catch (error) {
    if (!isDataCloneError(error)) {
      throw error;
    }

    const { client: sanitizedClient, didSanitize } = sanitizePersistedClient(client, {
      dropAuthSessionQueries: true,
      dropOrphanedPendingMutations: true,
      dropLegacyKeywordMutations: true,
      convertLegacyResponsibleMutations: true,
      dropUncloneableQueries: true,
      dropUncloneableMutations: true,
    });

    if (!didSanitize) {
      throw error;
    }

    await set(PERSIST_KEY, sanitizedClient);
  }
}

export function shouldDehydrateQuery(query: { queryKey: readonly unknown[] }): boolean {
  return (
    query.queryKey[0] !== AUTH_SESSION_QUERY_KEY &&
    defaultShouldDehydrateQuery(query as Parameters<typeof defaultShouldDehydrateQuery>[0])
  );
}

export async function clearPersistedShiftbookClientState(): Promise<void> {
  const value = await get<PersistedClient>(PERSIST_KEY);
  if (!value) {
    return;
  }

  const { client, didSanitize } = sanitizePersistedClient(value, {
    dropAuthSessionQueries: true,
    dropShiftbookQueries: true,
    dropAllMutations: true,
  });
  if (didSanitize) {
    await writePersistedClient(client);
  }
}

export const persister: NonNullable<
  PersistQueryClientProviderProps['persistOptions']
>['persister'] = {
  persistClient: async (client) => {
    try {
      await writePersistedClient({
        ...client,
        workspaceOwner: getSessionOwner(),
      } as PersistedClient);
    } catch {
      // Persistence is optional; the live QueryClient remains available in memory.
    }
  },
  restoreClient: async () => {
    let value: PersistedClient | undefined;
    try {
      value = await get<PersistedClient>(PERSIST_KEY);
    } catch {
      return undefined;
    }
    if (!value) return undefined;

    const persistedOwner = (value as PersistedClient & { workspaceOwner?: string }).workspaceOwner;
    const { client, didSanitize } = sanitizePersistedClient(value, {
      dropAuthSessionQueries: true,
      dropShiftbookQueries: !persistedOwner || persistedOwner !== getPreviousSessionOwner(),
      dropOrphanedPendingMutations: true,
      dropLegacyKeywordMutations: true,
      convertLegacyResponsibleMutations: true,
    });
    if (didSanitize) {
      try {
        await writePersistedClient(client);
      } catch {
        // Use the sanitized cache in memory even when its rewrite is unavailable.
      }
    }

    return client;
  },
  removeClient: async () => {
    try {
      await del(PERSIST_KEY);
    } catch {
      // IndexedDB may be unavailable; TanStack must still finish cache cleanup.
    }
  },
};

export const persistOptions: PersistQueryClientProviderProps['persistOptions'] = {
  persister,
  maxAge: 1000 * 60 * 60 * 12,
  dehydrateOptions: {
    shouldDehydrateQuery,
    // Persist only paused mutations (offline queue). Non-paused pending
    // mutations cannot be resumed after restore and can get stuck as "syncing".
    shouldDehydrateMutation: (mutation) => mutation.state.isPaused,
  },
};

export function bindOnlineManagerToNavigator() {
  if (typeof window === 'undefined') return;

  onlineManager.setEventListener((setOnline) => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  });
}
